// @ts-nocheck
import * as React from 'react';
import { ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCcw, RotateCcw } from 'lucide-react';

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMsg: string;
}

export class ErrorBoundary extends React.Component<Props, State> {
  private handleGlobalError = (event: ErrorEvent) => {
    const msg = event.message || event.error?.message || '';
    if (
      msg.toLowerCase().includes('websocket') ||
      msg.toLowerCase().includes('web-socket') ||
      msg.toLowerCase().includes('hmr') ||
      msg.toLowerCase().includes('sockjs') ||
      msg.toLowerCase().includes('socket') ||
      msg.toLowerCase().includes('vite') ||
      msg.toLowerCase().includes('closed without opened')
    ) {
      if (event.preventDefault) event.preventDefault();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      return true;
    }
  };

  private handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reasonStr = event.reason?.message || event.reason?.toString() || '';
    if (
      reasonStr.toLowerCase().includes('websocket') ||
      reasonStr.toLowerCase().includes('web-socket') ||
      reasonStr.toLowerCase().includes('hmr') ||
      reasonStr.toLowerCase().includes('sockjs') ||
      reasonStr.toLowerCase().includes('socket') ||
      reasonStr.toLowerCase().includes('vite') ||
      reasonStr.toLowerCase().includes('closed without opened')
    ) {
      if (event.preventDefault) event.preventDefault();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
      return;
    }

    console.error('Unhandled async promise rejection caught by ErrorBoundary:', event.reason);
    if (event.reason instanceof Error) {
      this.setState({
        hasError: true,
        errorMsg: `Async Error: ${event.reason.message}`
      });
    } else if (typeof event.reason === 'string') {
      this.setState({
        hasError: true,
        errorMsg: `Async Error: ${event.reason}`
      });
    }
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorMsg: ''
    };
  }

  public componentDidMount() {
    window.addEventListener('error', this.handleGlobalError);
    window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  public componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError);
    window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
  }

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, errorMsg: error?.message || 'Unexpected application runtime error' };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught component error:', error, errorInfo);
  }

  private handleSoftReset = () => {
    this.setState({ hasError: false, errorMsg: '' });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-zinc-950 flex flex-col items-center justify-center p-4 text-white">
          <div className="bg-zinc-900 border border-red-900/50 p-8 rounded-xl max-w-md w-full flex flex-col items-center text-center shadow-2xl">
            <div className="w-16 h-16 bg-red-900/20 rounded-full flex items-center justify-center mb-6">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Application Recovered</h1>
            <p className="text-zinc-400 text-sm mb-6">
              The application safely caught an execution error. Your design data is safe in memory and storage.
            </p>
            <div className="bg-zinc-950 w-full p-3 rounded text-xs font-mono text-red-400 text-left mb-6 overflow-auto max-h-32 border border-zinc-800 break-words">
              {this.state.errorMsg || 'Unknown rendering error'}
            </div>
            <div className="flex flex-col gap-2.5 w-full">
              <button 
                onClick={this.handleSoftReset} 
                className="flex items-center gap-2 bg-[#1473E6] hover:bg-blue-600 px-6 py-2.5 rounded-md font-bold text-sm transition-colors w-full justify-center text-white cursor-pointer"
              >
                <RotateCcw className="w-4 h-4" /> Try Resuming Session
              </button>
              <button 
                onClick={() => window.location.reload()} 
                className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 px-6 py-2 rounded-md font-semibold text-xs transition-colors w-full justify-center text-zinc-300 cursor-pointer"
              >
                <RefreshCcw className="w-3.5 h-3.5" /> Reload Application Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

